import React, { useEffect, useState } from 'react';
import { GroupBox } from '../shared/GroupBox';
import { Button } from '../ui/button';
import { 
  RefreshCw, FileText, CheckCircle, AlertTriangle, 
  XCircle, DollarSign, TrendingUp, TrendingDown
} from 'lucide-react';
import { toast } from 'sonner';
import { mondaySettlingApi } from '../../lib/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { format } from 'date-fns';

export function ReconciliationPage() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    clientBalances: true,
    unbalanced: true,
    orphaned: false,
    balanced: false
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const result = await mondaySettlingApi.getReconciliation();
      setData(result);
    } catch (error) {
      toast.error('Failed to load reconciliation data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const exportPDF = () => {
    if (!data) return;

    const doc = new jsPDF('p', 'mm', 'a4');
    let yPos = 15;

    // Title
    doc.setFontSize(18);
    doc.setTextColor(212, 175, 55);
    doc.text('Settlement Reconciliation Report', 14, yPos);
    yPos += 10;

    // Generated date
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated: ${format(new Date(data.generatedAt), 'PPpp')}`, 14, yPos);
    yPos += 10;

    // Summary section
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.text('Summary', 14, yPos);
    yPos += 6;

    const summaryData = [
      ['Total Settlements', data.summary.totalSettlements],
      ['Balanced Entries', data.summary.balancedEntries],
      ['Unbalanced Entries', data.summary.unbalancedEntries],
      ['Orphaned Entries', data.summary.orphanedEntries],
      ['System Balanced', data.summary.systemBalanced ? 'YES' : 'NO'],
      ['Total Client Balances', `₹${data.summary.totalClientBalances.toLocaleString()}`],
      ['Total P&L Balance', `₹${data.summary.totalPLBalance.toLocaleString()}`]
    ];

    autoTable(doc, {
      startY: yPos,
      head: [['Metric', 'Value']],
      body: summaryData,
      theme: 'grid',
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
      styles: { fontSize: 9 }
    });

    yPos = doc.lastAutoTable.finalY + 10;

    // Client Balances
    doc.setFontSize(12);
    doc.text('Client Balances', 14, yPos);
    yPos += 6;

    const clientData = data.clientBalances.map(c => [
      c.clientName,
      c.isSystemAccount ? c.systemAccountType : '-',
      c.entryCount,
      `₹${c.toReceiveTotal.toLocaleString()}`,
      `₹${c.toPayTotal.toLocaleString()}`,
      `₹${c.totalAmount.toLocaleString()}`
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['Client', 'Type', 'Entries', 'To Receive', 'To Pay', 'Net Balance']],
      body: clientData,
      theme: 'grid',
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
      styles: { fontSize: 8 }
    });

    // Unbalanced entries if any
    if (data.unbalancedEntries.length > 0) {
      yPos = doc.lastAutoTable.finalY + 10;

      if (yPos > 250) {
        doc.addPage();
        yPos = 15;
      }

      doc.setFontSize(12);
      doc.setTextColor(220, 38, 38);
      doc.text('Unbalanced Entries (Requires Attention)', 14, yPos);
      yPos += 6;

      const unbalancedData = data.unbalancedEntries.map(e => [
        e.entryId.substring(0, 20) + '...',
        e.entryCount,
        `₹${e.total.toLocaleString()}`
      ]);

      autoTable(doc, {
        startY: yPos,
        head: [['Entry ID', 'Count', 'Imbalance']],
        body: unbalancedData,
        theme: 'grid',
        headStyles: { fillColor: [220, 38, 38], textColor: [255, 255, 255] },
        styles: { fontSize: 8 }
      });
    }

    doc.save(`reconciliation_${format(new Date(), 'yyyyMMdd_HHmmss')}.pdf`);
    toast.success('PDF exported');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-yellow-500" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center text-gray-500 py-8">
        Failed to load data. Please try again.
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-yellow-500">Settlement Reconciliation Report</h1>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={exportPDF}>
            <FileText className="w-4 h-4 mr-2" />
            Export PDF
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className={`p-4 rounded-lg border ${data.summary.systemBalanced ? 'bg-green-900/20 border-green-500' : 'bg-red-900/20 border-red-500'}`}>
          <div className="flex items-center gap-2">
            {data.summary.systemBalanced ? (
              <CheckCircle className="w-5 h-5 text-green-500" />
            ) : (
              <XCircle className="w-5 h-5 text-red-500" />
            )}
            <span className="text-sm text-gray-400">System Status</span>
          </div>
          <div className={`text-xl font-bold ${data.summary.systemBalanced ? 'text-green-500' : 'text-red-500'}`}>
            {data.summary.systemBalanced ? 'BALANCED' : 'UNBALANCED'}
          </div>
        </div>

        <div className="p-4 rounded-lg border border-gray-700 bg-gray-800/50">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-yellow-500" />
            <span className="text-sm text-gray-400">Total Settlements</span>
          </div>
          <div className="text-xl font-bold text-white">{data.summary.totalSettlements}</div>
        </div>

        <div className="p-4 rounded-lg border border-gray-700 bg-gray-800/50">
          <div className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-500" />
            <span className="text-sm text-gray-400">Balanced Entries</span>
          </div>
          <div className="text-xl font-bold text-green-500">{data.summary.balancedEntries}</div>
        </div>

        <div className={`p-4 rounded-lg border ${data.summary.unbalancedEntries > 0 ? 'bg-red-900/20 border-red-500' : 'border-gray-700 bg-gray-800/50'}`}>
          <div className="flex items-center gap-2">
            <AlertTriangle className={`w-5 h-5 ${data.summary.unbalancedEntries > 0 ? 'text-red-500' : 'text-gray-500'}`} />
            <span className="text-sm text-gray-400">Unbalanced</span>
          </div>
          <div className={`text-xl font-bold ${data.summary.unbalancedEntries > 0 ? 'text-red-500' : 'text-gray-500'}`}>
            {data.summary.unbalancedEntries}
          </div>
        </div>
      </div>

      {/* P&L Account Summary */}
      {data.plAccountSummary && (
        <GroupBox title="P&L Account Summary" className="border-yellow-500/50">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 p-4">
            <div>
              <span className="text-sm text-gray-400">Account</span>
              <div className="font-semibold">{data.plAccountSummary.clientName}</div>
            </div>
            <div>
              <span className="text-sm text-gray-400">Total Entries</span>
              <div className="font-semibold">{data.plAccountSummary.entryCount}</div>
            </div>
            <div>
              <span className="text-sm text-gray-400">To Receive</span>
              <div className="font-semibold text-green-500">
                ₹{data.plAccountSummary.toReceiveTotal.toLocaleString()}
              </div>
            </div>
            <div>
              <span className="text-sm text-gray-400">To Pay</span>
              <div className="font-semibold text-red-500">
                ₹{Math.abs(data.plAccountSummary.toPayTotal).toLocaleString()}
              </div>
            </div>
          </div>
        </GroupBox>
      )}

      {/* Client Balances */}
      <GroupBox 
        title={`Client Balances (${data.clientBalances.length})`}
        className="border-blue-500/50"
      >
        <div className="p-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => toggleSection('clientBalances')}
            className="mb-2"
          >
            {expandedSections.clientBalances ? 'Collapse' : 'Expand'}
          </Button>
          
          {expandedSections.clientBalances && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-700">
                    <th className="text-left py-2 px-3">Client</th>
                    <th className="text-center py-2 px-3">Type</th>
                    <th className="text-right py-2 px-3">Entries</th>
                    <th className="text-right py-2 px-3">To Receive</th>
                    <th className="text-right py-2 px-3">To Pay</th>
                    <th className="text-right py-2 px-3">Net Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {data.clientBalances.map((client, idx) => (
                    <tr key={idx} className={`border-b border-gray-800 ${client.isSystemAccount ? 'bg-yellow-900/10' : ''}`}>
                      <td className="py-2 px-3">
                        {client.clientName}
                        {client.isSystemAccount && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-500">
                            {client.systemAccountType}
                          </span>
                        )}
                      </td>
                      <td className="text-center py-2 px-3 text-gray-400">
                        {client.isSystemAccount ? 'System' : 'Client'}
                      </td>
                      <td className="text-right py-2 px-3">{client.entryCount}</td>
                      <td className="text-right py-2 px-3 text-green-500">
                        {client.toReceiveTotal > 0 && (
                          <span className="flex items-center justify-end gap-1">
                            <TrendingUp className="w-3 h-3" />
                            ₹{client.toReceiveTotal.toLocaleString()}
                          </span>
                        )}
                      </td>
                      <td className="text-right py-2 px-3 text-red-500">
                        {client.toPayTotal < 0 && (
                          <span className="flex items-center justify-end gap-1">
                            <TrendingDown className="w-3 h-3" />
                            ₹{Math.abs(client.toPayTotal).toLocaleString()}
                          </span>
                        )}
                      </td>
                      <td className={`text-right py-2 px-3 font-semibold ${
                        client.totalAmount > 0 ? 'text-green-500' : 
                        client.totalAmount < 0 ? 'text-red-500' : 'text-gray-400'
                      }`}>
                        ₹{client.totalAmount.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </GroupBox>

      {/* Unbalanced Entries */}
      {data.unbalancedEntries.length > 0 && (
        <GroupBox 
          title={`⚠️ Unbalanced Entries (${data.unbalancedEntries.length})`}
          className="border-red-500"
        >
          <div className="p-2">
            <p className="text-sm text-red-400 mb-3">
              These entries don't have matching P&L contra entries or have incorrect amounts.
            </p>
            
            {data.unbalancedEntries.map((entry, idx) => (
              <div key={idx} className="mb-4 p-3 bg-red-900/10 rounded-lg border border-red-500/30">
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-mono text-gray-400">
                    {entry.entryId}
                  </span>
                  <span className="text-red-500 font-semibold">
                    Imbalance: ₹{entry.total.toLocaleString()}
                  </span>
                </div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-red-500/20">
                      <th className="text-left py-1">Type</th>
                      <th className="text-left py-1">Client</th>
                      <th className="text-left py-1">Description</th>
                      <th className="text-right py-1">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entry.entries.map((e, i) => (
                      <tr key={i} className="border-b border-gray-800">
                        <td className="py-1">{e.accountType}</td>
                        <td className="py-1">{e.clientName}</td>
                        <td className="py-1">{e.displayName}</td>
                        <td className={`py-1 text-right ${e.amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          ₹{e.amount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </GroupBox>
      )}

      {/* Orphaned Entries */}
      {data.orphanedEntries.length > 0 && (
        <GroupBox 
          title={`Orphaned Entries (${data.orphanedEntries.length})`}
          className="border-orange-500"
        >
          <div className="p-2">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => toggleSection('orphaned')}
              className="mb-2"
            >
              {expandedSections.orphaned ? 'Collapse' : 'Expand'}
            </Button>
            
            {expandedSections.orphaned && (
              <>
                <p className="text-sm text-orange-400 mb-3">
                  These entries have no entryId and cannot be paired with other entries.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-700">
                      <th className="text-left py-2">Type</th>
                      <th className="text-left py-2">Client</th>
                      <th className="text-left py-2">Description</th>
                      <th className="text-right py-2">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.orphanedEntries.map((e, idx) => (
                      <tr key={idx} className="border-b border-gray-800">
                        <td className="py-2">{e.accountType}</td>
                        <td className="py-2">{e.clientName}</td>
                        <td className="py-2">{e.displayName}</td>
                        <td className={`py-2 text-right ${e.amount >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                          ₹{e.amount.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </GroupBox>
      )}

      {/* Balanced Entries (collapsed by default) */}
      <GroupBox 
        title={`Balanced Entries (${data.balancedEntries.length})`}
        className="border-green-500/50"
      >
        <div className="p-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => toggleSection('balanced')}
            className="mb-2"
          >
            {expandedSections.balanced ? 'Collapse' : 'Expand'}
          </Button>
          
          {expandedSections.balanced && (
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {data.balancedEntries.map((entry, idx) => (
                <div key={idx} className="p-2 bg-green-900/10 rounded border border-green-500/20 text-xs">
                  <div className="flex justify-between items-center mb-1">
                    <span className="font-mono text-gray-400">{entry.entryId.substring(0, 30)}...</span>
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                  <div className="flex gap-4">
                    {entry.entries.map((e, i) => (
                      <span key={i} className={e.amount >= 0 ? 'text-green-400' : 'text-red-400'}>
                        {e.clientName}: ₹{e.amount.toLocaleString()}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </GroupBox>

      {/* Footer */}
      <div className="text-center text-xs text-gray-500">
        Report generated at {format(new Date(data.generatedAt), 'PPpp')}
      </div>
    </div>
  );
}

export default ReconciliationPage;
